import React, { useState } from "react";
import { all_routes } from "../../../router/all_routes";
import { Link, useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { getNavigationUrl } from "../../../../utils/helpers/setAndGetUrlParams";
import { MdLockReset } from "react-icons/md";
import { handleModalAction } from "../../../../utils/helpers/handleModalAction";
import ResetPasswordModal from "../../../../core/modals/ResetPasswordModal";
import { useFunctionalityAccess } from "../../../../core/common/wrapper/HasFunctionalityAccess";
import { Switch } from "@mui/material";
import { Typography } from "antd";
import StagePasswordModal from "../../../../core/common/crmComponents/StagePasswordModal";
import { useMutation } from "@tanstack/react-query";
import { verifyUserApi } from "../../../../utils/helpers/apiHelpers";
import { toast } from "react-toastify";
import UserApi from "../api/manageUserApi";
import queryClient from "../../../../config/queryClient";
import {
  EDITED_SUCCESS_MSG,
  ERROR_MSG,
} from "../../../../core/data/constants/constant";
import { useSelector } from "react-redux";

const UserDetailsHeader = ({ userData: user }) => {
  const route = all_routes;
  const navigate = useNavigate();
  const canGenerateUserPassword = useFunctionalityAccess(
    "allowUserPasswordGenration"
  );
  const canDisableUser = useFunctionalityAccess("disableUser");

  const [checked, setChecked] = useState(user?.disabled || false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const loggedInUser = useSelector((state) => state.auth.user);

  const mutationUpdate = useMutation({
    mutationFn: ({ id, userData, isCurrentlyDisabled }) => {
      const apiCall = isCurrentlyDisabled
        ? UserApi.disableUser
        : UserApi.updateUser;
      const updatedData = {
        ...userData,
        disabled: isCurrentlyDisabled,
      };
      return apiCall(id, updatedData);
    },
    onSuccess: (response) => {
      if (response?.status) {
        setChecked(response?.data?.disabled);
      }

      queryClient.invalidateQueries("users");
      queryClient.invalidateQueries("user");
      toast.success(
        response?.data?.message ? response?.data?.message : EDITED_SUCCESS_MSG
      );
    },
    onError: (error) => {
      console.log("error is ", error);

      toast.error((error.response?.data?.msg || ERROR_MSG).toUpperCase());
      setChecked(user.disabled); // revert toggle on error
    },
  });

  const mutationVerifyUser = useMutation({
    mutationFn: (data) => verifyUserApi(data),
    onSuccess: () => {
      mutationUpdate.mutate({
        id: user._id,
        isCurrentlyDisabled: !user.disabled,
        userData: {
          companyId: user?.ofCompany?._id,
        },
      });

      setPassword("");
      setShowPasswordModal(false);
    },
    onError: (error) => {
      console.log("error is ", error);

      toast.error(
        error.response?.data?.msg || "Verification failed. Please try again."
      );
      setChecked(true); // revert toggle to previous state
    },
  });

  const handleChange = (event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      // User is being enabled
      if (user.status === "active") {
        toast.error("Please make the user inactive first.");
        return;
      } else {
        setShowPasswordModal(true);
      }
    } else {
      // User is being disabled
      setShowPasswordModal(true);
    }
  };

  const handlePasswordSubmit = () => {
    if (!password) {
      toast.error("Please enter your password.");
      return;
    }
    mutationVerifyUser.mutate({ email: loggedInUser.email, password });
  };

  return (
    <div className="col-md-12">
      {/* Contact User */}
      <div className="contact-head">
        <div className="row justify-content-between  align-items-center">
          <div className="col-sm-6">
            <ul className="contact-breadcrumb">
              <li>
                <div
                  onClick={() => navigate(getNavigationUrl(route.manageusers))}
                  className="navigate-link"
                >
                  <i className="ti ti-arrow-narrow-left" />
                  Users
                </div>
              </li>
              <li className="text-capitalize">{user?.name}</li>
            </ul>
          </div>
          {canDisableUser && (
            <div className="col-md-6 d-flex justify-content-end gap-2 align-items-center">
              <Typography className="text-end">
                <p className="fw-bold">{checked ? "Enable" : "Disable"} User</p>
              </Typography>
              <Switch
                checked={checked}
                onChange={handleChange}
                inputProps={{ "aria-label": "controlled" }}
                color="error"
              />
            </div>
          )}
        </div>
      </div>
      <div className="card">
        <div className="card-body pb-2">
          <div className="d-flex align-items-center justify-content-between flex-wrap">
            <div className="d-flex align-items-center mb-2">
              {/* <div className="avatar avatar-xxl me-3 flex-shrink-0 border p-2">
                <ImageWithBasePath src={user?.logo} alt="Image" />
              </div> */}
              <div>
                <div>
                  <h5 className="mb-1 text-capitalize">{user?.name}</h5>
                  {user?.address?.street &&
                    user?.address?.city &&
                    user?.address?.state &&
                    user?.address?.zipcode && (
                      <p className="mb-2">
                        <i className="ti ti-map-pin-pin me-1"></i>
                        {`${user.address.street}, ${user.address.city} ${user.address.state}, ${user.address.zipcode}`}
                      </p>
                    )}

                  {user?.rating && (
                    <p className="d-inline-flex align-items-center mb-0">
                      <i className="fa-solid fa-star text-warning me-1"></i>{" "}
                      {user?.rating}
                    </p>
                  )}
                </div>

                {user?.role?.name && (
                  <p className="mb-2">
                    <span className="fw-bold">Role:</span>{" "}
                    <span className="text-capitalize">
                      {user?.role?.name?.split("-").join(" ")}
                    </span>{" "}
                  </p>
                )}
                {user?.ofCompany?.name && (
                  <p className="mb-2">
                    <span className="fw-bold">Company:</span>{" "}
                    {user?.ofCompany?.name}
                  </p>
                )}
              </div>
            </div>
            <div className="contacts-action">
              {canGenerateUserPassword && (
                <>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id="collapse-tooltip">Reset password</Tooltip>
                    }
                  >
                    <Link
                      className="btn-icon fs-5"
                      onClick={() => {
                        handleModalAction("resetPasswordModal", "show");
                      }}
                    >
                      <MdLockReset />
                    </Link>
                  </OverlayTrigger>
                  <ResetPasswordModal user={user} />
                </>
              )}
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="collapse-tooltip">Edit User</Tooltip>}
              >
                <Link
                  to={route.userDetails.replace(":id", `edit/${user?._id}`)}
                  className="btn-icon"
                >
                  <i className="ti ti-edit-circle" />
                </Link>
              </OverlayTrigger>
            </div>
          </div>
        </div>
      </div>
      {/* /Contact User */}

      <StagePasswordModal
        show={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        password={password}
        setPassword={setPassword}
        isPasswordVisible={isPasswordVisible}
        togglePasswordVisibility={() =>
          setIsPasswordVisible(!isPasswordVisible)
        }
        onSubmit={handlePasswordSubmit}
        isLoading={mutationVerifyUser.isLoading}
      />
    </div>
  );
};

export default UserDetailsHeader;
